import { useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import ConnectionStatus from '@/components/atoms/Accordion/templates/ConnectionStatusAccordion';
import ActiveFieldset from '@/components/atoms/Fieldset/templates/IntegrationsFieldset/ActiveFieldset';
import DeleteFieldset from '@/components/atoms/Fieldset/templates/IntegrationsFieldset/DeleteFieldset';
import { useAccordion } from '@/hooks/useAccordion';
import { getPermissions } from '@/redux/permissions/selectors';
import { AccordionType, InfobipRcsIntegration } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

import InfobipRcsConnect from './fieldsets/InfobipRcsConnect/InfobipRcsConnect';
import InfobipRcsInfo from './fieldsets/InfobipRcsInfo/InfobipRcsInfo';

/**
 * InfobipSms integration configuration component
 *
 * Displays configuration settings for Infobip SMS integration including:
 * - Connection status and settings
 * - Integration connection setup (if not connected) or information panel (if connected)
 * - Basic management options
 */
const IntegrationInfobipRcs = () => {
  const { t } = useTranslation();
  const stateRef = useRef(null);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );
  const { toggleAccordion, integration, accordions, registerAccordion } =
    useAccordion<InfobipRcsIntegration>(canWrite);

  // If integration is not loaded, render nothing
  if (!integration) {
    return null;
  }

  const hasApiKey = Boolean(integration?.config?.apikey);
  const hasBaseUrl = Boolean(integration?.config?.base_url);
  const hasSenderName = Boolean(integration?.config?.sender_name);
  const hasWebhookSecret = Boolean(integration?.config?.webhook_secret);
  // Check if integration is connected based on number and webhook_secret presence
  // These fields are populated by the integrations controller when connection is established
  const isConnected = hasApiKey && hasBaseUrl && hasSenderName && hasWebhookSecret;

  if (!isConnected) {
    return (
      <div className="flex flex-col gap-4">
        <InfobipRcsConnect integration={integration} />
        <DeleteFieldset integration={integration} />
      </div>
    );
  }

  return (
    <>
      <ConnectionStatus
        type={AccordionType.VERIFICATION_STATUS}
        registerAccordion={registerAccordion}
        status={integration?.status}
        toggleAccordion={toggleAccordion}
        conSettingsRef={accordions?.current[AccordionType.CONNECTION_SETTINGS]?.ref}
        stateRef={stateRef.current}
      />
      <div className="flex flex-col gap-4">
        <InfobipRcsInfo integration={integration} />
        <ActiveFieldset
          integration={integration}
          title={t('integrations.status.title')}
          subtitle={t('integrations.status.subtitle')}
        />
        <DeleteFieldset integration={integration} disabled={!canWrite} />
      </div>
    </>
  );
};

export default IntegrationInfobipRcs;
